import { cn, UzButton } from '@utazzo/ui';
import { ChevronRight, CornerDownRight, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useMarketingFieldVisibility } from '../use-marketing-reports';

import {
  GRAND_TOTAL_ROW,
  GRID_TABLE,
  numberTone,
  REPORT_HEAD_CELL,
  useGroupOpen,
} from './month-table';

import type { ContactabilityRow, MarketingCampaignContactabilityResponse } from '@utazzo/shared';

import { ReportSection } from '@/features/reports/components/ReportSection';

const count = (value: number): string => value.toLocaleString('en-IN');
const pct = (value: number | null): string => (value === null ? '-' : `${value.toFixed(0)}%`);
/**
 * Conversion rates sit well under 1% (2 of 588 leads), where the whole-number
 * rounding above prints a misleading "0%". Keep up to two decimals, trimmed.
 */
const pctFine = (value: number | null): string =>
  value === null ? '-' : `${Number(value.toFixed(2))}%`;

/** Lost % is derived here rather than stored - one rounding rule, one place. */
const lostPct = (row: ContactabilityRow): number | null =>
  row.queries > 0 ? (row.lost / row.queries) * 100 : null;

/** Header band shared by every table in the report. */
const HEAD = REPORT_HEAD_CELL;

/**
 * Total Leads | Lost % | Contacted % | Active % | Contact to Active % |
 * WhatsApp to Active % | Converted % | Sum of Lost | Sum of Active | Converted,
 * each coloured by what it means. Converted is the leads now Won. Active % is
 * leads that ever reached `active` over total; Contact to Active % is of the
 * contacted leads how many went active - the same pair as the overview cards.
 */
/** One data column: its catalog key (for hiding), header text and per-row cell. */
interface ColDef {
  key: string;
  header: string;
  render: (row: ContactabilityRow) => { text: string; tone: string };
}

/**
 * Column catalog. `key` doubles as the hideable field key where one exists
 * (contactedPct / activePct / contactToActivePct); the structural columns use
 * keys that are not in the catalog, so they are never hidden.
 */
const ALL_COLUMNS: ColDef[] = [
  {
    key: 'queries',
    header: 'Total Leads',
    render: (r) => ({ text: count(r.queries), tone: numberTone(r.queries) }),
  },
  {
    key: 'lostPct',
    header: 'Lost %',
    render: (r) => ({ text: pct(lostPct(r)), tone: numberTone(lostPct(r), 'bad') }),
  },
  {
    key: 'contactedPct',
    header: 'Contacted %',
    render: (r) => ({ text: pct(r.contactedPct), tone: numberTone(r.contactedPct, 'good') }),
  },
  {
    key: 'activePct',
    header: 'Active %',
    render: (r) => ({
      text: pct(r.activePct ?? null),
      tone: numberTone(r.activePct ?? null, 'good'),
    }),
  },
  {
    key: 'contactToActivePct',
    header: 'Contact to Active %',
    render: (r) => ({
      text: pct(r.contactToActivePct ?? null),
      tone: numberTone(r.contactToActivePct ?? null, 'good'),
    }),
  },
  {
    key: 'whatsAppToActivePct',
    header: 'WhatsApp to Active %',
    render: (r) => ({
      text: pct(r.whatsAppToActivePct ?? null),
      tone: numberTone(r.whatsAppToActivePct ?? null, 'good'),
    }),
  },
  {
    key: 'wonPct',
    header: 'Converted %',
    render: (r) => ({
      text: pctFine(r.wonPct ?? null),
      tone: numberTone(r.wonPct ?? null, 'good'),
    }),
  },
  {
    key: 'lost',
    header: 'Sum of Lost',
    render: (r) => ({ text: count(r.lost), tone: numberTone(r.lost, 'bad') }),
  },
  {
    key: 'active',
    header: 'Sum of Active',
    render: (r) => ({ text: count(r.active ?? 0), tone: numberTone(r.active ?? 0, 'good') }),
  },
  {
    // Which campaign the conversions came from: leads currently Won.
    key: 'won',
    header: 'Converted',
    render: (r) => ({ text: count(r.won ?? 0), tone: numberTone(r.won ?? 0, 'good') }),
  },
];

const Cells = ({
  row,
  cols,
  muted,
}: {
  row: ContactabilityRow;
  cols: ColDef[];
  muted?: boolean;
}): React.JSX.Element => (
  <>
    {cols.map((col) => {
      const { text, tone } = col.render(row);
      return (
        <td
          key={col.key}
          className={cn(
            'px-3 py-1.5 text-right whitespace-nowrap tabular-nums',
            muted ? 'text-muted-foreground' : tone,
          )}
        >
          {text}
        </td>
      );
    })}
  </>
);

const skeleton = (
  <div className="space-y-2 py-2">
    {[0, 1, 2].map((index) => (
      <div key={index} className="bg-muted h-8 animate-pulse rounded" />
    ))}
  </div>
);

/**
 * "Campaign wise Contactibility" - its own report, not a third table on the
 * region/agent section: it keys on the Meta ad campaign captured on the lead,
 * which only paid channels carry.
 *
 * Rows group by lead SOURCE with the campaigns that fed it nested underneath,
 * so a channel's campaigns always add up to the channel total ("Not set"
 * catches its organic / offline leads rather than dropping them).
 *
 * Campaign lists run long, hence the filter box and expand-all: the filter also
 * matches the channel name, and typing auto-reveals the matches without needing
 * every group opened first. Expand-all doubles as the export path - the tab CSV
 * reads the rendered DOM, so collapsed campaigns would otherwise be left out.
 */
export const CampaignContactabilityTable = ({
  data,
  loading,
}: {
  data: MarketingCampaignContactabilityResponse | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const groupOpen = useGroupOpen();
  const { isHidden } = useMarketingFieldVisibility();
  const cols = useMemo(() => ALL_COLUMNS.filter((c) => !isHidden(c.key)), [isHidden]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const needle = filter.trim().toLowerCase();
  const channels = useMemo(() => {
    if (!data) return [];
    if (!needle) return data.channels;
    return data.channels.flatMap((channel) => {
      // A channel-name match keeps the whole group; otherwise keep only the
      // campaigns that match, so the parent still shows its true total.
      if (channel.name.toLowerCase().includes(needle)) return [channel];
      const campaigns = channel.campaigns.filter((campaign) =>
        campaign.name.toLowerCase().includes(needle),
      );
      return campaigns.length > 0 ? [{ ...channel, campaigns }] : [];
    });
  }, [data, needle]);

  const allExpanded = channels.length > 0 && channels.every((channel) => expanded.has(channel.key));
  const toggleAll = (): void =>
    setExpanded(allExpanded ? new Set() : new Set(channels.map((channel) => channel.key)));

  const actions = !loading && data && (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2" />
        <input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter campaigns"
          aria-label="Filter campaigns"
          className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary h-8 w-48 rounded-md border pr-2 pl-7 text-xs outline-none"
        />
      </div>
      <UzButton variant="outline" size="sm" onClick={toggleAll} disabled={channels.length === 0}>
        {allExpanded ? 'Collapse all' : 'Expand all'}
      </UzButton>
    </div>
  );

  return (
    <ReportSection title="Campaign wise Contactibility" actions={actions}>
      {loading || !data ? (
        skeleton
      ) : channels.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          {needle ? `No campaign matches "${filter.trim()}".` : 'No leads in this range.'}
        </p>
      ) : (
        <table className={cn('w-full min-w-md text-[15px]', GRID_TABLE)}>
          <thead>
            <tr>
              <th className={cn(HEAD, 'text-left')}>Name</th>
              {cols.map((col) => (
                <th key={col.key} className={cn(HEAD, 'text-right')}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {channels.map((channel) => {
              // While filtering, matches are revealed without needing a click -
              // a hidden match reads as "no result".
              const open = groupOpen(needle !== '' || expanded.has(channel.key));
              return (
                <React.Fragment key={channel.key}>
                  <tr className="bg-muted/40 font-semibold">
                    <th
                      scope="row"
                      className="text-foreground px-3 py-1.5 text-left whitespace-nowrap"
                    >
                      <button
                        type="button"
                        onClick={() => toggle(channel.key)}
                        title={open ? 'Hide campaigns' : 'Show campaigns'}
                        className="flex items-center gap-1.5"
                      >
                        <ChevronRight
                          className={cn(
                            'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
                            open && 'rotate-90',
                          )}
                        />
                        {channel.name}
                        <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
                          {channel.campaigns.length}
                        </span>
                      </button>
                    </th>
                    <Cells row={channel} cols={cols} />
                  </tr>
                  {open &&
                    channel.campaigns.map((campaign) => (
                      <tr key={`${channel.key}-${campaign.key}`}>
                        <th
                          scope="row"
                          className="text-muted-foreground px-3 py-1.5 text-left font-normal whitespace-nowrap"
                        >
                          <span className="flex items-center gap-1.5 pl-5">
                            <CornerDownRight className="h-3.5 w-3.5 shrink-0" />
                            {campaign.name}
                          </span>
                        </th>
                        <Cells row={campaign} cols={cols} muted />
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
            {/* Always the unfiltered grand total - a filtered subtotal presented
                as "Grand Total" would misread as the whole range. */}
            <tr className={GRAND_TOTAL_ROW}>
              <th scope="row" className="text-foreground px-3 py-2 text-left whitespace-nowrap">
                {data.total.name}
              </th>
              <Cells row={data.total} cols={cols} />
            </tr>
          </tbody>
        </table>
      )}
      {!loading && data && (
        <p className="text-muted-foreground mt-3 text-xs">
          Campaigns come from the ad campaign captured on the lead at import, grouped under the
          source they arrived on. &ldquo;Not set&rdquo; is that source&apos;s leads with no campaign
          recorded (organic, offline, or created before campaign tracking), kept so each
          channel&apos;s campaigns add up to its total. Contacted % counts leads where a follow-up
          recorded a connected call, not merely an attempt. Active % is the leads that ever reached
          the Active stage over total leads, and Contact to Active % is how many of the contacted
          leads went Active. WhatsApp to Active % is, of the leads that reached Active, how many had
          a customer WhatsApp reply (a chat or a reply to a template) on or before going Active -
          the share of the active pipeline WhatsApp drove. Sum of Active is the raw count behind
          those percentages. Converted is the leads from that campaign that are Won today, and
          Converted % is that over total leads - which campaigns the bookings came from.
          {needle !== '' && ' Grand Total covers every channel, not just the filtered rows.'}
        </p>
      )}
    </ReportSection>
  );
};
